// Hand-written SDK for the custom Curlec checkout mutations (createPaymentOrder /
// verifyPayment). These live in PaymentMutations.Custom.cs on the API and are NOT
// part of the generated CRUD SDK surface — mirrors auth.sdk.ts's pattern exactly
// (constructor(private api), raw {success,error,message,data} envelope).
import { Injectable } from '@angular/core';
import { GraphqlApiService } from '../../api/graphql-api.service';

export type CheckoutPaymentType = 'deposit' | 'full' | 'next' | 'balance' | 'increase';

export interface PaymentOrderResult {
  paymentId: number;
  orderId: string;
  keyId: string;
  /** Sen (smallest MYR unit) — what Curlec's checkout.js `amount` option expects. */
  amount: number;
  currency: string;
  prefillName: string;
  prefillEmail: string;
  prefillContact: string;
}

export interface CheckoutEnvelope<T> {
  success: boolean;
  error?: string | null;
  message?: string | null;
  data?: T | null;
}

const PAYMENT_ORDER_FIELDS = `
  paymentId orderId keyId amount currency prefillName prefillEmail prefillContact
`;

@Injectable({ providedIn: 'root' })
export class CheckoutSdk {
  constructor(private api: GraphqlApiService) {}

  /** Server resolves the amount for each type — never send one. 'next' = the next scheduled instalment. */
  async createPaymentOrder(
    bookingId: number,
    paymentType: CheckoutPaymentType,
    bookingScheduleId?: number | null,
    /** 'increase' only — the order-change increase; the server caps it at what is still due. */
    amount?: number | null,
  ): Promise<CheckoutEnvelope<PaymentOrderResult>> {
    const q = `mutation CreatePaymentOrder($bookingId:Long!,$paymentType:String!,$bookingScheduleId:Long,$amount:Decimal){
      createPaymentOrder(bookingId:$bookingId,paymentType:$paymentType,bookingScheduleId:$bookingScheduleId,amount:$amount){
        success error message
        data { ${PAYMENT_ORDER_FIELDS} }
      }
    }`;
    const r = await this.api.request<{ createPaymentOrder: CheckoutEnvelope<PaymentOrderResult> }>(q, {
      bookingId, paymentType, bookingScheduleId: bookingScheduleId ?? null, amount: amount ?? null,
    });
    return r.createPaymentOrder;
  }

  /**
   * A VENDOR repaying their guarantee-fund clawback on one booking. The server derives the amount
   * from the ledger and writes the `clawback_repaid` row only once Curlec captures the payment.
   */
  async createClawbackPaymentOrder(bookingId: number, vendorId: number): Promise<CheckoutEnvelope<PaymentOrderResult>> {
    const q = `mutation CreateClawbackPaymentOrder($bookingId:Long!,$vendorId:Long!){
      createClawbackPaymentOrder(bookingId:$bookingId,vendorId:$vendorId){
        success error message
        data { ${PAYMENT_ORDER_FIELDS} }
      }
    }`;
    const r = await this.api.request<{ createClawbackPaymentOrder: CheckoutEnvelope<PaymentOrderResult> }>(q, { bookingId, vendorId });
    return r.createClawbackPaymentOrder;
  }

  /** Belt-and-braces confirmation after checkout.js's redirect back — idempotent with the webhook. */
  async verifyPayment(orderId: string, paymentId: string, signature: string): Promise<CheckoutEnvelope<unknown>> {
    const q = `mutation VerifyPayment($orderId:String!,$paymentId:String!,$signature:String!){
      verifyPayment(orderId:$orderId,paymentId:$paymentId,signature:$signature){ success error message }
    }`;
    const r = await this.api.request<{ verifyPayment: CheckoutEnvelope<unknown> }>(q, { orderId, paymentId, signature });
    return r.verifyPayment;
  }
}
